// A shareable picture of a slip for TikTok, Instagram and WhatsApp. Predictions only: each leg
// with the AI's chance and the chance of the whole slip landing. No odds, prices, stakes or
// bookmaker names, because social platforms ban gambling promotion.
import dayjs from "dayjs";
import logoUrl from "@/assets/logo.png";

const W = 1080;
const H = 1350; // 4:5 fits feeds and WhatsApp without cropping
const PAD = 72;
const FONT = '"Inter", "Poppins", system-ui, sans-serif';
const HOME = "#E59C98";
const AWAY = "#BDBDFB";
const GREEN = "#16a34a";
const INK = "#111827";
const MUTED = "#6b7280";

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// first line that fits `max`, ending in … when cut
function fit(ctx, text, max) {
  if (ctx.measureText(text).width <= max) return text;
  let out = text;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > max) out = out.slice(0, -1);
  return `${out.trimEnd()}…`;
}

/**
 * legs: [{label, match, kickoff (unix), side ('home'|'away'|null), p (0-100, or null when locked)}]
 * chance: the whole slip's chance (0-100), or null when locked or unpriced
 * Resolves to a PNG Blob.
 */
export async function slipImage({ legs, chance }) {
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // background: soft brand gradient
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#fdf2f1");
  bg.addColorStop(1, "#eef0ff");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // header: logo, then the headline
  const logo = await loadImage(logoUrl);
  let y = PAD;
  if (logo) {
    const lw = 360;
    const lh = (logo.height / logo.width) * lw;
    ctx.drawImage(logo, PAD, y, lw, lh);
    y += lh + 40;
  }
  ctx.fillStyle = INK;
  ctx.font = `700 64px ${FONT}`;
  ctx.fillText(legs.length > 1 ? `My ${legs.length}-leg slip` : "My pick", PAD, y + 56);
  y += 112;
  ctx.fillStyle = MUTED;
  ctx.font = `500 30px ${FONT}`;
  ctx.fillText("The AI's chance for each leg", PAD, y);
  y += 56;

  // legs: rows grow to fill the space when there are few (up to 190px), shrink to 122px when
  // there are many, then "+N more" for any that don't fit
  const footerTop = H - PAD - 250;
  const space = footerTop - y - 40;
  const rowH = Math.max(122, Math.min(190, Math.floor(space / Math.max(1, legs.length)) - 16));
  const scale = rowH / 122;
  const room = Math.max(1, Math.floor(space / (rowH + 16)));
  const shown = legs.length > room ? legs.slice(0, room - 1) : legs;
  const pillW = 170;
  for (const leg of shown) {
    roundRect(ctx, PAD, y, W - PAD * 2, rowH, 22);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    // team colour strip on the left
    ctx.fillStyle = leg.side === "home" ? HOME : leg.side === "away" ? AWAY : "#d1d5db";
    roundRect(ctx, PAD, y, 12, rowH, 6);
    ctx.fill();

    const textMax = W - PAD * 2 - pillW - 70;
    ctx.fillStyle = INK;
    ctx.font = `600 ${Math.round(38 * Math.min(scale, 1.2))}px ${FONT}`;
    ctx.fillText(fit(ctx, leg.label || "", textMax), PAD + 40, y + rowH / 2 - 6);
    ctx.fillStyle = MUTED;
    ctx.font = `500 ${Math.round(26 * Math.min(scale, 1.15))}px ${FONT}`;
    const when = leg.kickoff ? ` · ${dayjs.unix(leg.kickoff).format("ddd HH:mm")}` : "";
    ctx.fillText(fit(ctx, `${leg.match || ""}${when}`, textMax), PAD + 40, y + rowH / 2 + 38 * Math.min(scale, 1.2));

    // the chance, in a pill on the right
    const px = W - PAD - pillW - 24;
    const pillH = 70 * Math.min(scale, 1.25);
    roundRect(ctx, px, y + (rowH - pillH) / 2, pillW, pillH, pillH / 2);
    const known = leg.p !== null && leg.p !== undefined;
    ctx.fillStyle = known ? (leg.p >= 50 ? "#dcfce7" : "#f3f4f6") : "#eef0ff";
    ctx.fill();
    ctx.fillStyle = known ? (leg.p >= 50 ? GREEN : INK) : "#4338ca";
    ctx.font = `700 ${Math.round(40 * Math.min(scale, 1.25))}px ${FONT}`;
    ctx.textAlign = "center";
    ctx.fillText(known ? `${Math.round(leg.p)}%` : "?", px + pillW / 2, y + rowH / 2 + 14);
    ctx.textAlign = "left";
    y += rowH + 16;
  }
  if (shown.length < legs.length) {
    ctx.fillStyle = MUTED;
    ctx.font = `600 30px ${FONT}`;
    ctx.fillText(`+ ${legs.length - shown.length} more`, PAD + 8, y + 30);
  }

  // footer: the whole slip's chance, then the site
  y = footerTop;
  roundRect(ctx, PAD, y, W - PAD * 2, 160, 28);
  ctx.fillStyle = INK;
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.font = `600 30px ${FONT}`;
  ctx.fillText(legs.length > 1 ? "AI chance all legs land" : "AI chance it lands", PAD + 40, y + 62);
  ctx.font = `500 26px ${FONT}`;
  ctx.fillStyle = "#c7d2fe";
  ctx.fillText(chance === null || chance === undefined ? "See it free at numbersgame.uk" : "numbersgame.uk", PAD + 40, y + 112);
  ctx.textAlign = "right";
  ctx.fillStyle = "#ffffff";
  ctx.font = `700 76px ${FONT}`;
  ctx.fillText(chance === null || chance === undefined ? "?" : `${chance < 1 ? "<1" : Math.round(chance)}%`, W - PAD - 40, y + 108);
  ctx.textAlign = "left";

  ctx.fillStyle = MUTED;
  ctx.font = `500 24px ${FONT}`;
  ctx.textAlign = "center";
  ctx.fillText("18+ · BeGambleAware.org · Predictions, not guarantees", W / 2, H - PAD + 20);
  ctx.textAlign = "left";

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}
